import PropTypes from 'prop-types';
import React, { Fragment, memo } from 'react';
import classNames from 'classnames/bind';
import noop from 'lodash/noop';
import get from 'lodash/get';
import * as styles from './CouponCard.css';
import DateFormatter from '../../dates/DateFormatter/DateFormatter';
import getNumberOfDays from '../../dates/DateFormatter/GetNumberOfDays';
import { COUPONS_NO_CODE_REQUIRED } from '../../common/Constants';

const cx = classNames.bind(styles);

export function pad(num) {
    return `0${num}`.slice(-2);
}

export function getFormattedDate(enddate = '', showApiDate = false, displayCouponType = false) {
    /**
     * If the flag is enabled then show the date just by splitting
     * else show date localized previously existing functionality
     */
    if (showApiDate && enddate) {
        const formattedDate = enddate.match(/^(\d{4})-(\d{2})-(\d{2})/);

        if (!formattedDate || !formattedDate.length) {
            return '';
        }

        return displayCouponType
            ? `${formattedDate[2]}/${formattedDate[3]}`
            : `${formattedDate[2]}/${formattedDate[3]}/${formattedDate[1]}`;
    }

    const fullDate = enddate ? new Date(enddate) : new Date();

    return DateFormatter.formatWithOptions(fullDate);
}

export function getExpiryDate(offerEndDate, showApiDate, displayCouponType, timerLabel) {
    const daysRemaining =
        offerEndDate &&
        !isNaN(new Date(offerEndDate)) &&
        getNumberOfDays(offerEndDate.match(/^(\d{4})-(\d{2})-(\d{2})/)[0]);
    const timerLabelText = typeof timerLabel === 'string' ? timerLabel.toLowerCase() : '';
    if (timerLabelText === 'tomorrow' || timerLabelText === 'today') {
        return timerLabelText.toUpperCase();
    }
    switch (daysRemaining) {
        case '0':
            return 'TODAY';
        case '1':
            return 'TOMORROW';
        default:
            return getFormattedDate(offerEndDate, showApiDate, displayCouponType);
    }
}

const CouponCode = ({
    expirationDate,
    timerLabel,
    onCouponExpiration,
    couponData,
    funnel,
    displayCouponType,
    expiredInLessThanDay,
    isCouponDrawer,
}) => {
    const offerEndDate = get(couponData, 'content.offerEndDate', null);
    const promoCode = get(couponData, 'content.promoCode', null);
    let showCoupon = true;

    const isTimerNegative = () =>
        (timerLabel &&
            (timerLabel.hours < 0 || timerLabel.minutes < 0 || timerLabel.seconds < 0)) ||
        (expirationDate &&
            (expirationDate.hours < 0 || expirationDate.minutes < 0 || expirationDate.seconds < 0));

    const isExpiredInDay = expiredInLessThanDay && !isTimerNegative();

    /* istanbul ignore next */
    const renderExpirationDate = () => {
        const { hours, minutes, seconds } = expirationDate;

        /* istanbul ignore next */
        if (showCoupon && isTimerNegative()) {
            showCoupon = false;

            onCouponExpiration();
        }

        if (showCoupon && expiredInLessThanDay) {
            return isCouponDrawer ? (
                <>
                    <span className={cx('expiration-timer')}>{pad(hours)}</span>h
                    <span className={cx('expiration-timer')}> {pad(minutes)}</span>m
                    <span className={cx('expiration-timer')}> {pad(seconds)}</span>s
                </>
            ) : (
                'Today'
            );
        }

        if (showCoupon && timerLabel && timerLabel.seconds !== undefined) {
            return null;
        }

        /* istanbul ignore next */
        return showCoupon ? timerLabel : 'Expired';
    };

    const couponCodeHtml = promoCode ? (
        <h4 className={cx('codeNumber', funnel)}>
            Code:{' '}
            <strong data-automation-id="at-coupon-code">{couponData.content.promoCode}</strong>{' '}
        </h4>
    ) : (
        <span data-automation-id="at-coupon-code" className={cx('nocodeNecessaryClass')}>
            {COUPONS_NO_CODE_REQUIRED}
        </span>
    );

    const expiryLabel = 'expires';
    /* istanbul ignore next */
    const couponValidThroughHtml =
        offerEndDate && !isNaN(new Date(offerEndDate)) ? (
            <h5 className={cx('codeValidTime')} data-automation-id="at-coupon-validity">
                {showCoupon || !isCouponDrawer ? expiryLabel : ''}{' '}
                <strong>{renderExpirationDate()}</strong>
            </h5>
        ) : (
            ''
        );

    let couponCodeContentHtml = (
        <div className={cx('couponCodeBlock')} data-automation-id="at-apply-remove-coupon-wrapper">
            {couponValidThroughHtml}
            {couponCodeHtml}
        </div>
    );

    if (displayCouponType) {
        /* istanbul ignore next */
        couponCodeContentHtml =
            offerEndDate && !isNaN(new Date(offerEndDate)) ? (
                <span className={cx('expiry')}>
                    {showCoupon || !isCouponDrawer ? expiryLabel : ''}
                    <span
                        className={cx('date', {
                            'date-lessThanDay': isExpiredInDay && !isCouponDrawer,
                        })}
                    >
                        {' '}
                        {renderExpirationDate()}{' '}
                    </span>
                </span>
            ) : (
                ''
            );
    }

    return couponCodeContentHtml;
};

CouponCode.propTypes = {
    couponData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    funnel: PropTypes.string,
    displayCouponType: PropTypes.bool,
    onCouponExpiration: PropTypes.func,
    expirationDate: PropTypes.object,
    timerLabel: PropTypes.object,
    expiredInLessThanDay: PropTypes.bool,
    isCouponDrawer: PropTypes.bool,
};

CouponCode.defaultProps = {
    couponData: [],
    funnel: '',
    displayCouponType: false,
    onCouponExpiration: noop,
    expirationDate: null,
    timerLabel: null,
    expiredInLessThanDay: false,
    isCouponDrawer: false,
};

export default memo(CouponCode);
